import ReactDom from 'react-dom';

import { Icon as BlueIcon } from '@blueprintjs/core';

import IconLogoImg from '@assets/svg/icon-logo.svg';

import { useMutation, useQuery, useQueryClient } from 'react-query';
import { createAdminMessage } from '@services/queries/messages';
import { useEffect, useRef, useState } from 'react';

import { getMessagesByThread } from '@services/queries/onboarding/getMessagesByThread';
import { IIdentificationRequest } from '@models/IdentificationRequests.model';
import { useHookstate } from '@hookstate/core';
import { ChatTextFormat } from '@pages/Onboarding/components/ChatTextFormat/ChatTextFormat';
import {
  Container,
  Overlay,
  Close,
  Header,
  Title,
  TitleContainer,
  Description,
  Messages,
  Message,
  Text,
  RightPoint,
  LeftPoint,
  AuthorContainer,
  AuthorImage,
  AuthorName,
  AuthorAutomated,
  AuthorBeamImage,
  TextMessage,
  DisabledText,
  SenderInfo,
  SenderName,
  SenderEmail,
} from './IdentificationRequestChat.styles';
import {
  ChatStatusDropDown,
  statusMenuDisplayState,
} from '../ChatStatusDropDown/ChatStatusDropDown';
import { IdentificationRequestsChatLoading } from './IdentificationRequestsChat.loading';

interface IIdentificationRequestChatRaw {
  identificationRequest: IIdentificationRequest;
  open: boolean;
  onClose: () => void;
}

export const IdentificationRequestChatRaw = ({
  identificationRequest,
  open,
  onClose,
}: IIdentificationRequestChatRaw) => {
  const portalDiv = document.getElementById('portal');

  const myRef = useRef<HTMLDivElement>(null);

  const queryClient = useQueryClient();

  const [text, setText] = useState('');
  const [isFullyBottom, setIsFullyBottom] = useState(true);
  const [isFirstRender, setIsFirstRender] = useState(true);
  const [sentNewMessage, setSentNewMessage] = useState(false);

  const openStatusMenu = useHookstate(statusMenuDisplayState);

  const { data: messages, isLoading } = useQuery(
    ['messages', identificationRequest?.threadId],
    async () => {
      return getMessagesByThread({
        threadId: identificationRequest.threadId,
      });
    },
    {
      refetchInterval: 5000,
    },
  );

  const scrollToBottom = () => {
    if (myRef.current) {
      myRef.current.scrollTop = myRef.current.scrollHeight;
    }
  };

  const mutationMessage = useMutation(createAdminMessage, {
    onSuccess: () => {
      queryClient.invalidateQueries(['messages']);
      setText('');
      setSentNewMessage(true);
    },
  });

  useEffect(() => {
    if (isFullyBottom || isFirstRender || sentNewMessage) {
      scrollToBottom();
      setIsFirstRender(false);
      setSentNewMessage(false);
    }
  }, [messages, isFullyBottom, isFirstRender]);

  const handleScroll = () => {
    if (myRef.current) {
      const container = myRef.current;
      setIsFullyBottom(
        container.scrollTop + container.clientHeight === container.scrollHeight,
      );
    }
  };

  return open && portalDiv
    ? ReactDom.createPortal(
        <Overlay>
          <Container onClick={() => openStatusMenu.set(false)}>
            {isLoading ? (
              <IdentificationRequestsChatLoading />
            ) : (
              <>
                <Header>
                  <TitleContainer>
                    <Title>Match Request</Title>
                    <Description>
                      {identificationRequest?.studioName}
                    </Description>
                  </TitleContainer>
                  <Close onClick={onClose}>
                    <BlueIcon icon="cross" color="#8F95B2" />
                  </Close>
                </Header>
                <ChatStatusDropDown
                  identificationRequest={identificationRequest}
                />
                <Messages ref={myRef} onScroll={handleScroll}>
                  {!isLoading && messages && (
                    <>
                      {messages.messages.map((message, idx) => {
                        const isAdmin = !!message.admin;
                        const isAutomated = !!message.automated;
                        const previousIsAdmin =
                          !!messages.messages[idx - 1]?.admin;

                        return (
                          <Message
                            key={message.id}
                            align={!isAdmin ? 'left' : 'right'}
                            isSequence={previousIsAdmin === isAdmin}
                          >
                            {previousIsAdmin !== isAdmin && (
                              <AuthorContainer>
                                {isAdmin ? (
                                  <AuthorBeamImage src={IconLogoImg} />
                                ) : (
                                  <AuthorImage />
                                )}

                                {isAdmin ? (
                                  <AuthorName>BeamON</AuthorName>
                                ) : (
                                  <SenderInfo>
                                    <SenderName>{message.sender}</SenderName>
                                    <SenderEmail>
                                      {message.senderEmail}
                                    </SenderEmail>
                                  </SenderInfo>
                                )}

                                {isAutomated && (
                                  <AuthorAutomated>(Automated)</AuthorAutomated>
                                )}
                              </AuthorContainer>
                            )}

                            <Text align={!isAdmin ? 'left' : 'right'}>
                              <TextMessage
                                align={!isAdmin ? 'left' : 'right'}
                                dangerouslySetInnerHTML={{
                                  __html: message.text,
                                }}
                              />
                              {!isAdmin ? <LeftPoint /> : <RightPoint />}
                            </Text>
                          </Message>
                        );
                      })}
                    </>
                  )}
                </Messages>
              </>
            )}

            {['resolved', 'closed'].includes(identificationRequest?.status) && (
              <DisabledText>
                This Request has been{' '}
                {identificationRequest?.status === 'resolved'
                  ? 'Resolved'
                  : 'Closed'}
                .
              </DisabledText>
            )}

            <ChatTextFormat
              text={text}
              setText={setText}
              onMutateMessage={() => {
                if (
                  text &&
                  !mutationMessage.isLoading &&
                  identificationRequest &&
                  !['resolved', 'closed'].includes(
                    identificationRequest?.status,
                  )
                ) {
                  mutationMessage.mutate({
                    text,
                    fileId: identificationRequest.fileId,
                    groupId: identificationRequest.groupId,
                    studioId: identificationRequest.studioId,
                    threadId: identificationRequest.threadId,
                  });
                }
                setText('');
              }}
              disabled={
                isLoading ||
                ['resolved', 'closed'].includes(identificationRequest?.status)
              }
            />
          </Container>
        </Overlay>,
        portalDiv,
      )
    : null;
};
